import { allows, type ResolvedEntitlements } from "@giftforward/entitlements";
import { buildCsv, downloadCsv } from "@/lib/csv";

/**
 * The recipient-upload template, served client-side for Step 2's "Send on different dates" download
 * and the manual "link to a list" download, mirroring the OneSchema templates the bulk-upload modal
 * (BulkUploadModal) uses — columns AND the Required/Optional guidance row (row 2 of OneSchema's own
 * Excel template).
 *
 * Why generated here and not fetched from OneSchema: the "Download Excel template" button lives
 * INSIDE the OneSchema importer iframe, the SDK exposes no way to trigger it from outside, and we
 * hold only the embed JWT credentials — not a OneSchema REST API key — so there is no way to pull
 * the real file. The columns below reproduce it from the same fields instead. Headers are the
 * human-readable labels (not the underlying keys) so a filled-in file maps cleanly on re-upload:
 * OneSchema matches uploaded columns to template columns by header text.
 *
 * The `guidance` flags here are the SOURCE OF TRUTH (GFG-449), matching the importer's
 * `templateOverrides` — NOT the OneSchema dashboard's own row 2, which still marks the last names
 * and Sender Email required. Keep these in step with BulkUploadModal's overrides, not the dashboard.
 */

/** One template column. `proOnly` columns exist only in the Pro/Enterprise template; `sendDate`
 *  columns are the per-recipient send schedule, dropped for a campaign that never sends. `guidance`
 *  is the Required/Optional hint shown in the template's second row. */
interface TemplateColumn {
  label: string;
  guidance: string;
  proOnly?: boolean;
  sendDate?: boolean;
}

// Column set and order. The proOnly columns are exactly what the Pro/Enterprise template adds over
// Starter — recipient country and the per-recipient send date/time — the same split BulkUploadModal
// keys its template choice on. Keep the columns AND the guidance in step with the OneSchema overrides
// (there is no API to derive them from).
const TEMPLATE_COLUMNS: TemplateColumn[] = [
  { label: "Recipient First Name", guidance: "Required" },
  { label: "Recipient Last Name", guidance: "Optional" },
  { label: "Recipient Email", guidance: "Required" },
  { label: "Recipient Company", guidance: "Optional" },
  { label: "Recipient Country", guidance: "Optional: Leave blank for USA", proOnly: true },
  { label: "Sender First Name", guidance: "Required" },
  { label: "Sender Last Name", guidance: "Optional" },
  { label: "Sender Email", guidance: "Optional" },
  { label: "Sender Signature", guidance: "Optional: John at Company Name" },
  { label: "Sender Company", guidance: "Required" },
  { label: "Send On Date", guidance: "Optional: mm/dd/yyyy", proOnly: true, sendDate: true },
  { label: "Send at Time", guidance: "Optional: hh:mm", proOnly: true, sendDate: true },
];

/**
 * True when the caller's plan gets the Pro/Enterprise template. The single source of truth for the
 * tier split, shared with BulkUploadModal so this download and the importer there never diverge: the
 * rich template is the one carrying the country + per-recipient send-date columns, so it follows the
 * entitlements those columns depend on rather than a plan name.
 */
export function recipientTemplateIsPro(ent: ResolvedEntitlements | undefined): boolean {
  return (
    !!ent &&
    allows(ent, "campaign_send", "scheduling", "per_recipient") &&
    allows(ent, "campaign_send", "regions", "international")
  );
}

/**
 * The tier-appropriate template: a header row plus ONE guidance row marking each column
 * Required/Optional (with format hints), mirroring the OneSchema Excel template's row 2 — the sender
 * fills their list in from the next row, typing over the guidance. `withSendDate=false` drops the
 * per-recipient send schedule columns, for a manual "link to a list" campaign that never sends.
 * Exported for testing.
 */
export function buildRecipientTemplateCsv(isPro: boolean, withSendDate: boolean): string {
  const columns = TEMPLATE_COLUMNS.filter(
    (c) => (isPro || !c.proOnly) && (withSendDate || !c.sendDate),
  );
  const guidanceRow: Record<string, string> = Object.fromEntries(
    columns.map((c) => [c.label, c.guidance]),
  );
  const csvColumns = columns.map((c) => ({
    label: c.label,
    value: (row: Record<string, string>) => row[c.label] ?? "",
  }));
  return buildCsv(csvColumns, [guidanceRow]);
}

/**
 * Download the recipient-upload template matching the caller's tier. `withSendDate` defaults true (the
 * gfg "Send on different dates" download wants the schedule columns); pass false for a manual "link to
 * a list" campaign, which never sends per-recipient and so has no use for them.
 */
export function downloadRecipientTemplate(
  ent: ResolvedEntitlements | undefined,
  opts?: { withSendDate?: boolean },
): void {
  const isPro = recipientTemplateIsPro(ent);
  const withSendDate = opts?.withSendDate ?? true;
  const filename = isPro
    ? "gift-recipient-template-pro.csv"
    : "gift-recipient-template-starter.csv";
  downloadCsv(filename, buildRecipientTemplateCsv(isPro, withSendDate));
}
